import { FC, useState } from 'react';
import { useLocation } from 'react-router';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import {
  TextField,
  IconButton,
  Box,
  Typography,
  useTheme,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  useMediaQuery,
  Button,
  Popover
} from '@mui/material';
import ModalAddSocialProfile from 'src/components/ModalAddSocialProfile';
import {
  IObject,
  TObjectStatusFilter,
  TObjectFilter,
  TStreamTypeFilter
} from 'src/models/objectManagement';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  reducerUpdateObjectType,
  reducerUpdateSelectedObjectStatus,
  reducerUpdateSelectedStreamType
} from 'src/features/objectManagement';
import ModalAddPostLink from 'src/components/ModalAddPostLink';
import ModalAddSocialProfileRetail from 'src/components/Retail/ModalAddSocialProfileRetail';
import ModalAddKeywordAndHashtagRetail from 'src/components/Retail/ModalAddKeywordAndHashtagRetail';
import { useSearchParams } from 'react-router-dom';
import { AddLink, AlternateEmail, Tag } from '@mui/icons-material';
import NotAllowedTooltip from 'src/components/NotAllowedTooltip';
interface Props {
  objectCount: any;
  onSearch: (keyword: string) => void;
  objectList: Array<IObject>;
}

export interface IOption {
  id: string;
  label: string;
  value: string;
}

export const objectTypeOptions: IOption[] = [
  {
    id: 'all',
    label: 'All data source',
    value: 'all'
  },
  {
    id: 'object',
    label: 'Object',
    value: 'object'
  },
  {
    id: 'entity',
    label: 'Entity based',
    value: 'entity'
  }
];

export const defaultStreamTypeOptions: IOption[] = [
  {
    id: 'all',
    label: 'All object type',
    value: 'all'
  },
  {
    id: 'keyword',
    label: 'Keyword',
    value: 'keyword'
  },
  {
    id: 'hashtag',
    label: 'Hashtag',
    value: 'hashtag'
  },
  {
    id: 'account',
    label: 'Account',
    value: 'account'
  }
];

export const objectStatusOptions: IOption[] = [
  {
    id: 'all',
    label: 'All status',
    value: 'all'
  },
  {
    id: 'pending',
    label: 'Pending',
    value: 'pending'
  },
  {
    id: 'running',
    label: 'Running',
    value: 'running'
  },
  {
    id: 'success',
    label: 'Success',
    value: 'success'
  },
  {
    id: 'error',
    label: 'Error',
    value: 'error'
  }
];

const objectOptions = [
  {
    type: 'account',
    title: 'Social Profile',
    description: 'Monitor a public social account',
    icon: <AlternateEmail color="primary" />
  },
  {
    type: 'keyword',
    title: 'Keyword & Hashtag',
    description: 'Monitor keyword or hashtag',
    icon: <Tag color="primary" />
  },
  {
    type: 'post_link',
    title: 'Post Link',
    description: 'Monitor one specific public post',
    icon: <AddLink color="primary" />
  }
];

const HeaderObjects: FC<Props> = ({ objectCount, onSearch, objectList }) => {
  const theme = useTheme();
  const [searchParams, _] = useSearchParams();
  const { customerType, role } = useAppSelector((state) => state.users.project);
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const {
    account,
    entityAccount,
    keyword,
    entityKeyword,
    accountQuota,
    keywordQuota
  } = objectCount;
  const { objectType, selectedStreamType, selectedObjectStatus } =
    useAppSelector((state) => state.objectManagement);
  const title = pathname.split('/')[2];
  const isEcommerceTab = searchParams.get('object_type');
  const isEcommerce = title === 'ecommerce' || isEcommerceTab === 'ecommerce';

  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openChooseObject, setOpenChooseObject] = useState<
    'account' | 'keyword' | 'post_link' | null
  >(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
    setOpenChooseObject(null);
  };

  // const objectListSocialProfile = useMemo((): Array<IObject> => {
  //   return objectList.filter((object) => object.streamType === 'account');
  // }, [objectList]);

  // const objectListKeywordAndHashtag = useMemo((): Array<IObject> => {
  //   return objectList.filter((object) => object.streamType === 'keyword');
  // }, [objectList]);

  const handleSearch = function (e: any) {
    const keyword = e.target.value;
    onSearch(keyword);
  };

  const handleChangeObjectType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(reducerUpdateObjectType(selectedValue as TObjectFilter));
  };

  const handleChangeStreamType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(
      reducerUpdateSelectedStreamType(selectedValue as TStreamTypeFilter)
    );
  };

  const handleChangeObjectStatus = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(
      reducerUpdateSelectedObjectStatus(selectedValue as TObjectStatusFilter)
    );
  };

  const isRetail = customerType === 'b2c';
  // const matchedMediaUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const streamTypeOptions = isRetail
    ? defaultStreamTypeOptions
    : [
        ...defaultStreamTypeOptions,
        {
          id: 'post_link',
          label: 'Post Link',
          value: 'post_link'
        }
      ];

  const isMember = role === 'member';

  return (
    <>
      <Stack
        direction={isRetail ? 'column' : 'row'}
        justifyContent={isRetail ? 'flex-start' : 'space-between'}
        alignItems={isRetail ? 'start' : 'center'}
        gap={isRetail ? 2 : 0}
      >
        <Stack direction="row" gap={2}>
          <TextField
            size="small"
            sx={{ flexGrow: 1, minWidth: { md: 300 } }}
            InputProps={{
              startAdornment: (
                <IconButton size="small">
                  <SearchTwoToneIcon />
                </IconButton>
              )
            }}
            placeholder="Search objects"
            onChange={handleSearch}
          />
          {!isEcommerce && (
            <Stack
              direction={'row'}
              spacing={2}
              width={{ xs: '100%', md: '450px' }}
            >
              <Select
                id="objectType"
                name="objectType"
                value={objectType}
                onChange={handleChangeObjectType}
                sx={{ width: '100%' }}
              >
                <MenuItem key="placeholder" id="placeholder" value="" disabled>
                  Data Source
                </MenuItem>
                {objectTypeOptions.map(({ id, value, label }) => (
                  <MenuItem key={id} id={id} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                id="streamType"
                name="streamType"
                value={selectedStreamType}
                onChange={handleChangeStreamType}
                sx={{ width: '100%' }}
              >
                <MenuItem key="placeholder" id="placeholder" value="" disabled>
                  Object Type
                </MenuItem>
                {streamTypeOptions.map(({ id, value, label }) => (
                  <MenuItem key={id} id={id} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                id="objectStatus"
                name="objectStatus"
                value={selectedObjectStatus}
                onChange={handleChangeObjectStatus}
                sx={{ width: '100%' }}
              >
                <MenuItem key="placeholder" id="placeholder" value="" disabled>
                  Object Status
                </MenuItem>
                {objectStatusOptions.map(({ id, value, label }) => (
                  <MenuItem key={id} id={id} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          mt={{ xs: 1, md: 0 }}
          spacing={theme.spacing(2)}
        >
          {isEcommerce ? (
            <ModalAddSocialProfile
              title="Store Account"
              // objectList={objectListSocialProfile}
              buttonLabel="Store Account"
              buttonProps={{
                variant: 'contained',
                sx: {
                  backgroundColor: theme.colors.primary.dark,
                  borderRadius: theme.spacing(1)
                }
              }}
            />
          ) : isMember ? (
            <NotAllowedTooltip>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.dark
                }}
              >
                + Add Object
              </Button>
            </NotAllowedTooltip>
          ) : (
            <Button
              variant="contained"
              onClick={handleOpen}
              sx={{
                backgroundColor: theme.palette.primary.dark
              }}
            >
              + Add Object
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap={'wrap'} gap={2}>
        <>
          {!isEcommerce && (
            <Box sx={{ display: 'flex', marginTop: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 14 }}>
                Account{' '}
                <Typography
                  component="span"
                  variant="h4"
                  color={theme.palette.primary.main}
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  {account}/{accountQuota}
                </Typography>
              </Typography>
              <Typography
                variant="h4"
                sx={{ marginLeft: 2, fontWeight: 600, fontSize: 14 }}
              >
                {isEcommerce ? 'Keyword' : 'Keyword & Hashtag'}{' '}
                <Typography
                  component="span"
                  variant="h4"
                  color={theme.palette.primary.main}
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  {keyword}/{keywordQuota}
                </Typography>
              </Typography>
            </Box>
          )}
          {!isEcommerce && (
            <>
              <Divider
                orientation={'vertical'}
                variant="middle"
                sx={{
                  border: '1px solid',
                  color: theme.palette.primary.main,
                  marginY: 0,
                  marginTop: '14px'
                }}
                flexItem
              />
              <Box sx={{ display: 'flex', marginTop: { xs: 0, md: 2 } }}>
                <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 14 }}>
                  Entity Account{' '}
                  <Typography
                    component="span"
                    variant="h4"
                    color={theme.palette.primary.main}
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    {entityAccount}
                  </Typography>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ marginLeft: 2, fontWeight: 600, fontSize: 14 }}
                >
                  Entity Keyword & Hashtag{' '}
                  <Typography
                    component="span"
                    variant="h4"
                    color={theme.palette.primary.main}
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    {entityKeyword}
                  </Typography>
                </Typography>
              </Box>
            </>
          )}
        </>
      </Stack>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: theme.spacing(2), maxWidth: '600px', width: '100%' }}>
          <Stack spacing={1}>
            <>
              {objectOptions
                .filter((item) =>
                  isRetail ? item.type !== 'post_link' : !!item.type
                )
                .map((row) => (
                  <Stack
                    direction="row"
                    spacing={1}
                    key={row.type}
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      ':hover': {
                        backgroundColor: theme.colors.primary.light
                      },
                      padding: theme.spacing(1)
                    }}
                    onClick={() =>
                      setOpenChooseObject(
                        row.type as 'account' | 'keyword' | 'post_link'
                      )
                    }
                  >
                    <Stack
                      sx={{
                        backgroundColor: theme.colors.primary.light,
                        borderRadius: theme.spacing(1),
                        padding: theme.spacing(1)
                      }}
                    >
                      {row.icon}
                    </Stack>
                    <Stack>
                      <Typography variant="h4">{row.title}</Typography>
                      <Typography variant="caption">
                        {row.description}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
            </>
          </Stack>
        </Box>
      </Popover>
      {openChooseObject === 'post_link' && !isRetail && (
        <ModalAddPostLink
          title="Post Link"
          buttonLabel="Post Link"
          buttonProps={{
            variant: 'contained',
            sx: {
              backgroundColor: theme.colors.primary.dark,
              borderRadius: theme.spacing(1)
            }
          }}
          showButton={false}
          onDone={handleClose}
          handleCloseModal={() => setOpenChooseObject(null)}
        />
      )}
      {openChooseObject === 'account' && (
        <ModalAddSocialProfileRetail
          buttonVariant="contained"
          showButton={false}
          onDone={handleClose}
          handleCloseModal={() => setOpenChooseObject(null)}
        />
      )}
      {openChooseObject === 'keyword' && (
        <ModalAddKeywordAndHashtagRetail
          streamType={null}
          buttonVariant="contained"
          showButton={false}
          onDone={handleClose}
          handleCloseModal={() => setOpenChooseObject(null)}
        />
      )}
    </>
  );
};

export default HeaderObjects;
